import DetailsCard from "@/components/DetailsCard";
import DetailsCardThreeColumnRow from "@/components/DetailsCardThreeColumnRow";
import MultiSectionDetailsCard from "@/components/MultiSectionDetailsCard";
import MetricGrid from "@/components/MetricGrid";
import { CardSkeleton } from "@/components/Skeleton";
import { METRIC_CARD_MIN_WIDTH_PX } from "@/components/MetricGrid/metricGridLayout";
import {
  buildApplicationsOverviewOptions,
  buildCareerOverviewMetrics,
  buildCareerPlanningOverviewOptions,
  buildCompensationOverviewOptions,
  buildPreferencesOverviewOptions,
  getCurrentCompensationProfile,
  getActiveGovernanceRoles,
  type CareerOverviewCareerProfile,
  type CareerOverviewCompensationProfile,
  type CareerOverviewGovernanceRole,
  type CareerOverviewJobApplication,
  type CareerOverviewJobPreferences,
  type CareerOverviewNavigate,
  type CareerOverviewPerson
} from "./careerOverviewUtils";

export type CareerOverviewProps = {
  person: CareerOverviewPerson;
  jobPreferences?: CareerOverviewJobPreferences | null;
  applications?: CareerOverviewJobApplication[];
  totalJobApplications?: number | null;
  totalActiveJobApplications?: number | null;
  compensationProfiles?: CareerOverviewCompensationProfile[];
  governanceRoles?: CareerOverviewGovernanceRole[];
  openJobPostCount?: number;
  canViewCareerPreferences: boolean;
  canManageJobPosts: boolean;
  canViewCompensationProfiles: boolean;
  canViewCareerPlan?: boolean;
  careerProfile?: CareerOverviewCareerProfile | null;
  careerProfileLoading?: boolean;
  canViewJobPostings: boolean;
  canViewPersonCareer: boolean;
  loading?: boolean;
  navigate: CareerOverviewNavigate;
};

export default function CareerOverview({
  person,
  jobPreferences,
  applications = [],
  totalJobApplications,
  totalActiveJobApplications,
  compensationProfiles = [],
  governanceRoles = [],
  openJobPostCount = 0,
  canViewCareerPreferences,
  canManageJobPosts,
  canViewCompensationProfiles,
  canViewCareerPlan = false,
  careerProfile,
  careerProfileLoading = false,
  canViewJobPostings,
  canViewPersonCareer,
  loading = false,
  navigate
}: CareerOverviewProps) {
  const pgaId = person.pgaId;
  const compensationProfile = getCurrentCompensationProfile(compensationProfiles);
  const activeGovernanceRoleCount = getActiveGovernanceRoles(governanceRoles).length;

  const metrics = buildCareerOverviewMetrics({
    person,
    jobPreferences,
    totalActiveJobApplications,
    canViewPersonCareer,
    canViewCareerPreferences,
    canManageJobPosts,
    canViewCompensationProfiles,
    canViewJobPostings,
    openJobPostCount,
    activeGovernanceRoleCount,
    compensationProfile
  }).map((metric) => ({ ...metric, loading }));

  const hasPreferenceCards = canViewCareerPreferences && !!pgaId;
  const hasApplicationsCard = canManageJobPosts && !!pgaId;
  const hasCareerPlanning = canViewCareerPlan && !!pgaId;
  const hasCompensationCard = canViewCompensationProfiles;
  const hasSummaryCards = hasCareerPlanning || hasCompensationCard || hasPreferenceCards || hasApplicationsCard;
  const careerPlanningOptions = hasCareerPlanning
    ? buildCareerPlanningOverviewOptions(pgaId, careerProfile, navigate)
    : [];
  const compensationOptions = hasCompensationCard
    ? buildCompensationOverviewOptions(person, compensationProfile, navigate)
    : [];

  return (
    <div className="flex flex-col min-w-0" data-testid="career-overview">
      {!!metrics.length && <MetricGrid metrics={metrics} minCardWidth={METRIC_CARD_MIN_WIDTH_PX} />}

      {hasSummaryCards && (
        <DetailsCardThreeColumnRow className={metrics.length ? "mt-6" : "pt-12"}>
          {hasCareerPlanning && careerProfileLoading ? (
            hasCompensationCard ? (
              <DetailsCard heading="Compensation" options={compensationOptions} />
            ) : (
              <CardSkeleton className="w-full" width={100} rows={3} />
            )
          ) : hasCareerPlanning && hasCompensationCard ? (
            <MultiSectionDetailsCard
              sections={[
                { heading: "Career Profile", options: careerPlanningOptions },
                { heading: "Compensation", options: compensationOptions }
              ]}
            />
          ) : hasCareerPlanning ? (
            <DetailsCard heading="Career Profile" options={careerPlanningOptions} />
          ) : hasCompensationCard ? (
            <DetailsCard heading="Compensation" options={compensationOptions} />
          ) : null}
          {hasPreferenceCards && (
            <DetailsCard
              heading="Job Preferences"
              options={buildPreferencesOverviewOptions(
                jobPreferences,
                pgaId,
                navigate,
                person.primarySection?.careerConsultants
              )}
            />
          )}
          {hasApplicationsCard && (
            <DetailsCard
              heading="Applications"
              options={buildApplicationsOverviewOptions(
                applications,
                totalJobApplications,
                totalActiveJobApplications,
                pgaId,
                navigate
              )}
            />
          )}
        </DetailsCardThreeColumnRow>
      )}
    </div>
  );
}
